import { Text, TextFading } from '@yes.technology/react-toolkit'
import { useCallback, useId, useMemo, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18n-lite'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import GanttCard from './cards'
import {
  buildCaptionColorMap,
  DEFAULT_ROW_HEIGHT,
  ganttDefaultCaptions,
  resolveGanttCardBackgroundColor
} from './Gantt.utils'
import GanttArrow from './GanttArrow'
import GanttCategories from './GanttCategories'
import {
  ARROW_STROKE_COLOR,
  ArrowsSvg,
  CurrentMonthHighlight,
  GanttBody,
  GanttBodyTimelineScroll,
  GanttFooter,
  GanttFooterTimelineFrame,
  GanttHeader,
  GanttHiddenHorizontalScrollbar,
  GanttRoot,
  GridCell,
  GridRow,
  GridRows,
  MonthHeaderCell,
  MonthHeaderGrid,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarRow,
  TimelineCanvas,
  TimelineMilestoneLine
} from './GanttChart.styles'
import { GanttChartProps } from './GanttChart.types'
import GanttMilestones from './GanttMilestones'
import {
  useGanttArrowsLayout,
  useGanttBodyFiller,
  useGanttCardLayout,
  useGanttCardSelection,
  useGanttFooterMilestones,
  useGanttScrollSync,
  useGanttTimelineMetrics
} from './hooks'

const isGanttRowStriped = (globalRowIndex: number) => globalRowIndex % 2 === 1

const GanttChart = ({ props }: GanttChartProps) => {
  const {
    year = new Date().getFullYear(),
    categories,
    captions = ganttDefaultCaptions,
    cards,
    arrows = [],
    footer,
    highlightCurrentMonth = true,
    months: monthsProp,
    sidebarTitle,
    styleMap
  } = props
  const styleProps = useStyleMaps(styleMap)
  const { language: locale } = useTranslation()
  const rawMarkerId = useId()
  const arrowHeadMarkerId = `gantt-arrow-head-${rawMarkerId.replace(/[^a-zA-Z0-9-_]/g, '')}`

  const captionColorById = useMemo(
    () => buildCaptionColorMap(captions),
    [captions]
  )

  const {
    headerScrollRef,
    bodyScrollRef,
    footerScrollRef,
    timelineCanvasRef,
    syncHorizontalScroll
  } = useGanttScrollSync()

  const {
    timelineWidth,
    normalizedMonthColumns,
    months,
    currentMonthIndex,
    currentMonthHighlight
  } = useGanttTimelineMetrics({
    bodyScrollRef,
    year,
    highlightCurrentMonth,
    locale,
    months: monthsProp
  })

  const { cardLayoutMap, rowList, totalBodyHeight, cardOrderMap } =
    useGanttCardLayout({
      cards,
      categories,
      year,
      timelineWidth
    })

  const { orderedArrows, sourceSpineXMap } = useGanttArrowsLayout({
    arrows,
    cards,
    cardLayoutMap,
    cardOrderMap
  })

  const { footerMilestones } = useGanttFooterMilestones({
    footer,
    year,
    timelineWidth
  })

  const { fillerRowHeights, timelineCanvasHeight } = useGanttBodyFiller({
    bodyScrollRef,
    totalBodyHeight
  })

  const {
    toggleCardSelection,
    setHoveredCard,
    isCardSelected,
    isArrowEmphasized
  } = useGanttCardSelection()

  const handleCardClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const id = event.currentTarget.getAttribute('data-gantt-card-id')
      if (id) toggleCardSelection(id)
    },
    [toggleCardSelection]
  )

  const handleCardMouseEnter = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const id = event.currentTarget.getAttribute('data-gantt-card-id')
      if (id) setHoveredCard(id)
    },
    [setHoveredCard]
  )

  const handleCardMouseLeave = useCallback(() => {
    setHoveredCard(null)
  }, [setHoveredCard])

  const resolvedSidebarTitle = sidebarTitle ?? ''
  const resolvedFooterLeftLabel = footer?.leftLabel ?? ''

  return (
    <GanttRoot style={styleProps}>
      <GanttHeader>
        <SidebarHeader>
          <TextFading>
            <Text variant='body-emphasis-05'>{resolvedSidebarTitle}</Text>
          </TextFading>
        </SidebarHeader>
        <GanttHiddenHorizontalScrollbar
          ref={headerScrollRef}
          scrollMode='horizontal'
          style={{ flex: 1, minWidth: 0, width: '100%', minHeight: 'unset' }}
          onScroll={(event) =>
            syncHorizontalScroll(event.currentTarget.scrollLeft)
          }
        >
          <MonthHeaderGrid
            data-testid='gantt-month-header'
            $columns={normalizedMonthColumns}
            $width={timelineWidth}
          >
            {months.map((month, index) => (
              <MonthHeaderCell
                key={`gantt-month-${index}`}
                $isCurrent={index === currentMonthIndex}
                data-testid={`gantt-month-${index}`}
              >
                <Text variant='body-emphasis-05'>{month}</Text>
              </MonthHeaderCell>
            ))}
          </MonthHeaderGrid>
        </GanttHiddenHorizontalScrollbar>
      </GanttHeader>

      <GanttBody>
        <SidebarBody>
          <GanttCategories categories={categories} rowList={rowList} />
          {fillerRowHeights.map((height, fillerIndex) => {
            const globalRowIndex = rowList.length + fillerIndex
            return (
              <SidebarRow
                key={`filler-sidebar-row-${fillerIndex}`}
                $isStriped={isGanttRowStriped(globalRowIndex)}
                $height={height}
              />
            )
          })}
        </SidebarBody>

        <GanttBodyTimelineScroll
          ref={bodyScrollRef}
          scrollMode='all'
          style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: 'unset'
          }}
          onScroll={(event) =>
            syncHorizontalScroll(event.currentTarget.scrollLeft)
          }
        >
          <TimelineCanvas
            ref={timelineCanvasRef}
            $height={timelineCanvasHeight}
            $width={timelineWidth}
          >
            {currentMonthHighlight && (
              <CurrentMonthHighlight
                data-testid='gantt-current-month-highlight'
                $left={currentMonthHighlight.left}
                $width={currentMonthHighlight.width}
              />
            )}
            {footerMilestones.map((milestone, index) => (
              <TimelineMilestoneLine
                key={milestone.renderKey}
                $left={milestone.left}
                data-testid={`gantt-milestone-body-line-${index}`}
              />
            ))}

            <GridRows>
              {rowList.map((row) => (
                <GridRow
                  key={`grid-row-${row.globalRowIndex}`}
                  $height={DEFAULT_ROW_HEIGHT}
                  $isStriped={isGanttRowStriped(row.globalRowIndex)}
                  $columns={normalizedMonthColumns}
                  $width={timelineWidth}
                  data-testid={`gantt-grid-row-${row.globalRowIndex}`}
                >
                  {months.map((month, monthIndex) => (
                    <GridCell
                      key={`${row.globalRowIndex}-${month}`}
                      data-testid={`gantt-grid-cell-${row.globalRowIndex}-${monthIndex}`}
                    />
                  ))}
                </GridRow>
              ))}
              {fillerRowHeights.map((height, fillerIndex) => {
                const globalRowIndex = rowList.length + fillerIndex
                return (
                  <GridRow
                    key={`filler-grid-row-${fillerIndex}`}
                    $height={height}
                    $isStriped={isGanttRowStriped(globalRowIndex)}
                    $columns={normalizedMonthColumns}
                    $width={timelineWidth}
                  >
                    {months.map((month, monthIndex) => (
                      <GridCell
                        key={`filler-grid-cell-${fillerIndex}-${month}`}
                        data-testid={`gantt-grid-cell-filler-${fillerIndex}-${monthIndex}`}
                      />
                    ))}
                  </GridRow>
                )
              })}
            </GridRows>

            <ArrowsSvg viewBox={`0 0 ${timelineWidth} ${timelineCanvasHeight}`}>
              <defs>
                <marker
                  id={arrowHeadMarkerId}
                  markerWidth='6'
                  markerHeight='6'
                  refX='6'
                  refY='3'
                  orient='auto'
                >
                  <path d='M0,0 L6,3 L0,6 Z' fill={ARROW_STROKE_COLOR} />
                </marker>
              </defs>
              {orderedArrows.map((arrow) => {
                const source = cardLayoutMap[arrow.fromCardId]
                const target = cardLayoutMap[arrow.toCardId]
                if (!source || !target) return null

                const sourceSpineX =
                  sourceSpineXMap[arrow.fromCardId] ??
                  Math.max(8, source.left - 12)

                return (
                  <GanttArrow
                    key={arrow.id}
                    id={arrow.id}
                    arrowHeadMarkerId={arrowHeadMarkerId}
                    originAnchor={arrow.originAnchor}
                    source={source}
                    target={target}
                    sourceSpineX={sourceSpineX}
                    isEmphasized={isArrowEmphasized(arrow)}
                  />
                )
              })}
            </ArrowsSvg>

            {cards.map((card) => {
              const layout = cardLayoutMap[card.id]
              if (!layout) return null

              return (
                <GanttCard
                  key={card.id}
                  card={card}
                  layout={layout}
                  backgroundColor={resolveGanttCardBackgroundColor(
                    card,
                    captionColorById
                  )}
                  isSelected={isCardSelected(card.id)}
                  onClick={handleCardClick}
                  onMouseEnter={handleCardMouseEnter}
                  onMouseLeave={handleCardMouseLeave}
                />
              )
            })}
          </TimelineCanvas>
        </GanttBodyTimelineScroll>
      </GanttBody>

      <GanttFooter>
        <SidebarFooter>{resolvedFooterLeftLabel}</SidebarFooter>
        <GanttHiddenHorizontalScrollbar
          ref={footerScrollRef}
          scrollMode='horizontal'
          style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            width: '100%',
            minHeight: 'unset'
          }}
          onScroll={(event) =>
            syncHorizontalScroll(event.currentTarget.scrollLeft)
          }
        >
          <GanttFooterTimelineFrame $width={timelineWidth}>
            <GanttMilestones
              milestones={footerMilestones}
              timelineWidth={timelineWidth}
            />
          </GanttFooterTimelineFrame>
        </GanttHiddenHorizontalScrollbar>
      </GanttFooter>
    </GanttRoot>
  )
}

export default GanttChart
